import { Board } from './Board'
import { detectGroups, findAdjacentGarbage, Group } from './GroupDetector'
import { calcWaveScore, scoreToGarbage } from './ScoreCalc'
import { PieceGenerator, Piece } from './PieceGenerator'
import { RNG } from '../rng'
import {
  BOARD_COLS, BOARD_ROWS,
  SPAWN_COL, SPAWN_ROW,
  CELL_EMPTY, CELL_GARBAGE,
  SUB_OFFSETS,
  CHAIN_FLASH_DURATION,
  CHAIN_FALL_DURATION,
  CHAIN_DISPLAY_DURATION,
} from '../constants'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PieceState {
  color1: number    // main (pivot) block color
  color2: number    // sub block color
  col: number       // pivot column
  row: number       // pivot row
  rotation: number  // 0-3
}

export type Phase =
  | { kind: 'falling' }
  | { kind: 'chain_flash'; waveIndex: number; groups: Group[]; garbageCleared: Array<[number, number]>; totalScore: number; timer: number }
  | { kind: 'chain_fall';  waveIndex: number; totalScore: number; timer: number }
  | { kind: 'game_over' }

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns [mainPos, subPos] for a piece given its state. */
export function getPiecePositions(piece: PieceState): [[number, number], [number, number]] {
  const [dc, dr] = SUB_OFFSETS[piece.rotation]
  return [
    [piece.col, piece.row],
    [piece.col + dc, piece.row + dr],
  ]
}

// ─── GameLogic ────────────────────────────────────────────────────────────────

export class GameLogic {
  board:      Board
  current:    PieceState
  nextPiece:  Piece
  phase:      Phase = { kind: 'falling' }

  score:          number = 0
  scoreRemainder: number = 0    // carry-over for garbage calculation
  outgoingGarbage: number = 0   // garbage queued to send to opponent
  incomingGarbage: number = 0   // garbage queued to receive

  // Chain display info (read by renderer)
  lastChainCount:  number  = 0
  lastChainScore:  number  = 0
  chainJustEnded:  boolean = false
  chainDisplayTimer: number = 0

  softDropping: boolean = false
  spawnCount:   number  = 1     // incremented each time a new piece spawns (AI uses this)

  private gen:       PieceGenerator
  private garbageRng: RNG
  private dropTimer: number = 0
  private speed:     number   // cells per second (set by room)

  constructor(seed: number, speed: number) {
    this.speed      = speed
    this.gen        = new PieceGenerator(seed)
    this.garbageRng = new RNG(seed ^ 0xdeadbeef)
    this.board      = new Board()

    const first  = this.gen.next()
    this.current = { color1: first.color1, color2: first.color2, col: SPAWN_COL, row: SPAWN_ROW, rotation: 0 }
    this.nextPiece = this.gen.next()
  }

  // ─── Public input API (called by client / server tick) ───────────────────

  moveLeft()  { if (this.phase.kind === 'falling') this.tryMove(-1, 0) }
  moveRight() { if (this.phase.kind === 'falling') this.tryMove( 1, 0) }

  rotateCW()  { if (this.phase.kind === 'falling') this.tryRotate(1)  }
  rotateCCW() { if (this.phase.kind === 'falling') this.tryRotate(-1) }

  softDropStart() { this.softDropping = true  }
  softDropEnd()   { this.softDropping = false }

  hardDrop() {
    if (this.phase.kind !== 'falling') return
    while (this.tryMove(0, 1)) {}
    this.lockPiece()
  }

  /** Enqueue garbage from opponent. Dropped next time a piece locks. */
  receiveGarbage(count: number) {
    this.incomingGarbage += count
  }

  // ─── Main update loop ────────────────────────────────────────────────────

  update(dt: number): void {
    if (this.chainDisplayTimer > 0) this.chainDisplayTimer -= dt

    switch (this.phase.kind) {
      case 'falling':
        this.updateFalling(dt)
        break

      case 'chain_flash':
        this.phase.timer -= dt
        if (this.phase.timer <= 0) this.applyChainWave(this.phase)
        break

      case 'chain_fall':
        this.phase.timer -= dt
        if (this.phase.timer <= 0) this.checkNextWave(this.phase)
        break

      case 'game_over':
        break
    }
  }

  // ─── Private helpers ─────────────────────────────────────────────────────

  private updateFalling(dt: number): void {
    const effectiveSpeed = this.softDropping ? this.speed * 10 : this.speed
    const interval = 1 / effectiveSpeed

    this.dropTimer += dt
    if (this.dropTimer >= interval) {
      this.dropTimer -= interval
      if (!this.tryMove(0, 1)) {
        this.lockPiece()
      }
    }
  }

  private lockPiece(): void {
    const [[c1, r1], [c2, r2]] = getPiecePositions(this.current)
    this.board.set(c1, r1, this.current.color1)
    this.board.set(c2, r2, this.current.color2)
    this.board.applyGravity()

    const groups = detectGroups(this.board)
    if (groups.length > 0) {
      const garbageCleared = findAdjacentGarbage(this.board, groups)
      this.phase = {
        kind: 'chain_flash',
        waveIndex: 0,
        groups,
        garbageCleared,
        totalScore: 0,
        timer: CHAIN_FLASH_DURATION,
      }
    } else {
      this.dropIncomingGarbage()
      this.spawnNext()
    }
  }

  private applyChainWave(
    p: Extract<Phase, { kind: 'chain_flash' }>,
  ): void {
    // Remove popped cells
    for (const g of p.groups) {
      for (const [c, r] of g.cells) this.board.set(c, r, CELL_EMPTY)
    }
    for (const [c, r] of p.garbageCleared) this.board.set(c, r, CELL_EMPTY)

    const waveScore   = calcWaveScore(p.waveIndex, p.groups)
    const totalScore  = p.totalScore + waveScore

    this.board.applyGravity()

    this.phase = {
      kind: 'chain_fall',
      waveIndex: p.waveIndex,
      totalScore,
      timer: CHAIN_FALL_DURATION,
    }
  }

  private checkNextWave(
    p: Extract<Phase, { kind: 'chain_fall' }>,
  ): void {
    const groups = detectGroups(this.board)

    if (groups.length > 0) {
      const garbageCleared = findAdjacentGarbage(this.board, groups)
      this.phase = {
        kind: 'chain_flash',
        waveIndex: p.waveIndex + 1,
        groups,
        garbageCleared,
        totalScore: p.totalScore,
        timer: CHAIN_FLASH_DURATION,
      }
    } else {
      // Chain fully resolved
      this.score += p.totalScore
      const { garbage, remainder } = scoreToGarbage(p.totalScore, this.scoreRemainder)
      this.scoreRemainder   = remainder
      this.outgoingGarbage += garbage

      // Announce chain for UI
      if (p.waveIndex >= 1) {  // only display if actual chain (≥ 2 waves)
        this.lastChainCount   = p.waveIndex + 1
        this.lastChainScore   = p.totalScore
        this.chainJustEnded   = true
        this.chainDisplayTimer = CHAIN_DISPLAY_DURATION
      }

      this.dropIncomingGarbage()
      this.spawnNext()
    }
  }

  private dropIncomingGarbage(): void {
    if (this.incomingGarbage <= 0) return

    const count = this.incomingGarbage
    this.incomingGarbage = 0

    const fullRows = Math.floor(count / BOARD_COLS)
    const partial  = count % BOARD_COLS

    // Place garbage at the top rows — gravity will settle it on existing blocks.
    // Do NOT use shiftDown: it destroys existing content at the bottom.

    for (let r = 0; r < fullRows; r++) {
      const cols = this.garbageRng.shuffle([0, 1, 2, 3, 4, 5])
      for (let c = 0; c < BOARD_COLS; c++) {
        this.board.set(cols[c], r, CELL_GARBAGE)
      }
    }

    if (partial > 0) {
      const cols = this.garbageRng.shuffle([0, 1, 2, 3, 4, 5])
      for (let i = 0; i < BOARD_COLS; i++) {
        this.board.set(cols[i], fullRows, i < partial ? CELL_GARBAGE : CELL_EMPTY)
      }
    }

    // Settle garbage on top of existing blocks (gravity pulls toward row BOARD_ROWS-1)
    this.board.applyGravity()
  }

  private spawnNext(): void {
    const piece: PieceState = {
      color1: this.nextPiece.color1,
      color2: this.nextPiece.color2,
      col: SPAWN_COL,
      row: SPAWN_ROW,
      rotation: 0,
    }

    if (!this.isValidPosition(piece)) {
      this.phase = { kind: 'game_over' }
      return
    }

    this.current   = piece
    this.nextPiece = this.gen.next()
    this.dropTimer = 0
    this.softDropping = false
    this.spawnCount++
    this.phase     = { kind: 'falling' }
  }

  private tryMove(dc: number, dr: number): boolean {
    const moved = { ...this.current, col: this.current.col + dc, row: this.current.row + dr }
    if (this.isValidPosition(moved)) {
      this.current = moved
      return true
    }
    return false
  }

  private tryRotate(dir: 1 | -1): void {
    const newRot = ((this.current.rotation + dir + 4) % 4)

    // Wall-kick candidates: no kick → kick in rotation dir → kick other dir → floor kick
    const kicks: Array<[number, number]> = [
      [0,  0],
      [dir, 0],
      [-dir, 0],
      [0,  1],   // floor kick: push main block down so sub fits above
    ]

    for (const [dc, dr] of kicks) {
      const candidate: PieceState = {
        ...this.current,
        rotation: newRot,
        col: this.current.col + dc,
        row: this.current.row + dr,
      }
      if (this.isValidPosition(candidate)) {
        this.current = candidate
        return
      }
    }
    // Rotation blocked – do nothing
  }

  private isValidPosition(piece: PieceState): boolean {
    const [[c1, r1], [c2, r2]] = getPiecePositions(piece)
    return (
      this.board.inBounds(c1, r1) && this.board.isEmpty(c1, r1) &&
      this.board.inBounds(c2, r2) && this.board.isEmpty(c2, r2)
    )
  }

  // ─── Read-only helpers for renderer ──────────────────────────────────────

  /** Set of "col,row" strings for cells currently flashing (about to pop). */
  get flashingCells(): Set<string> {
    if (this.phase.kind !== 'chain_flash') return new Set()
    const s = new Set<string>()
    for (const g of this.phase.groups) {
      for (const [c, r] of g.cells) s.add(`${c},${r}`)
    }
    for (const [c, r] of this.phase.garbageCleared) s.add(`${c},${r}`)
    return s
  }

  /** Current chain wave number (1-based, 0 when not in a chain). */
  get currentChainCount(): number {
    if (this.phase.kind === 'chain_flash') return this.phase.waveIndex + 1
    if (this.phase.kind === 'chain_fall')  return this.phase.waveIndex + 1
    return 0
  }

  /** Column the piece would land on if dropped right now (for ghost rendering). */
  get ghostRow(): number {
    let r = this.current.row
    while (true) {
      const test: PieceState = { ...this.current, row: r + 1 }
      if (!this.isValidPosition(test)) break
      r++
    }
    return r
  }

  get isInputBlocked(): boolean {
    return this.phase.kind !== 'falling'
  }
}
